import { twoFactor, bindTwoFactorCsrf } from '../api/twoFactor';
import { ApiError } from './ApiError';
import { trackApiAction } from '../lib/analytics';
// Re-exported so the ~17 modules that import `ApiError` from `services/api`
// keep working; the class now lives in a dependency-free leaf module.
export { ApiError };
import type {
  PdlcDeliverable,
  PdlcFileRequestResult,
  SetPdlcTaskLinkInput,
  PdlcTaskFile,
  PdlcTaskFilePlan,
  PdlcTaskFilePlanRequest,
  ProductWatchStatus,
  WatchedProduct,
  ProductChaseView,
  LaunchTypeDropSettings,
  CreateProductChaseInput,
  UpdateProductChaseInput,
  NameProposal,
  MarketingRequest,
  MarketingRequestSettings,
  MarketingRequestStatus,
  CreateMarketingRequestInput,
  UpdateMarketingRequestInput,
  NameProposalQueueItem,
  ProductRenameRecord,
  NameProposalWriteResult,
  ProductLaunchDate,
  ProductLaunchDates,
  ClaimCaveat,
  CreateClaimCaveatInput,
  ResolveClaimCaveatInput,
  LaunchSetupInput,
  LaunchSetupPreview,
  LaunchSetupRule,
  LaunchSetupRun,
  SetLaunchDateInput,
  CreateNameProposalInput,
  UpdateNameProposalInput,
  AppMetricsResult,
  AppMetricsTrends,
  BugsnagErrorDetail,
  BugsnagErrorListResult,
  BugsnagErrorQuery,
  BugsnagProjectOption,
  BugsnagTriageRecord,
  BugsnagTriageRequest,
  CreateIssueFromBugsnagRequest,
  TrackedBugsnagProject,
  BackupListResponse,
  BackupRunStatus,
  InspectReport,
  DevDashboardConfig,
  DevDashboardRepoOption,
  DevIssueAssignableUser,
  DevIssueComment,
  DevIssueDetailResult,
  DevIssueListResult,
  DevIssueQuery,
  DevIssueState,
  DevProjectBoard,
  DevProjectOption,
  GithubConnectionStatus,
  Product,
  EvaluationSubmission,
  UpdateSubmissionAssignmentInput,
  SubmissionWithTemplate,
  CreateSubmissionInput,
  AutosaveRequest,
  AutosaveResponse,
  PaginatedResponse,
  FileMetadata,
  PresignedUploadResult,
  ProductImage,
  ProductImageRole,
  CreateUserInput,
  User,
  UserContext,
  LoginResponse,
  LoginRequest,
  ChangePasswordRequest,
  ChangePasswordResponse,
  CreateProductInput,
  ProductTaxonomy,
  UpdateProductTaxonomyInput,
  ApiKeyDisplay,
  CreateApiKeyInput,
  CreateApiKeyResponse,
  UpdateApiKeyInput,
  SubmissionSnapshot,
  SnapshotListItem,
  TaxonomySnapshot,
  TaxonomySnapshotListItem,
  PdlcDashboardResponse,
  PdlcMetricsResponse,
  PdlcStatusReadBack,
  AsanaClaimRun,
  AsanaConnectionStatus,
  MyPdlcTask,
  PdlcTask,
  AsanaProjectStory,
  AsanaTaskComment,
  PdlcTaskCommentSummary,
  AsanaAttachment,
  PdlcUnreadCounts,
  ProductCardId,
  AsanaMentionUser,
  AsanaMentionRef,
  ReportIssueRequest,
  ReportIssueResponse,
  ReportedIssueList,
  BugTrackerTask,
  BugTrackerSubtaskPolicy,
  BugTrackerSection,
  BugTrackerSourceProject,
  BugTrackerCategory,
  BugTrackerPhotometrics,
  BugTrackerPriority,
  BugTrackerPendingTag,
  BugTrackerTwStatus,
  BugTrackerTwTag,
  BugTrackerSupport,
  ThemeId,
  DivisionId,
  EffectiveDivisions,
  MyDocumentsView,
  SigningRequestOverviewRow,
  Supplier,
  CreateSupplierInput,
  UpdateSupplierInput,
  CreateSupplierContactInput,
  UpdateSupplierContactInput,
  Office,
  CreateOfficeInput,
  UpdateOfficeInput,
  Shipment,
  CreateShipmentInput,
  UpdateShipmentInput,
  ShipmentListFilters,
  TeamsConnectionStatus,
  TeamsAuthorizeUrl,
  TeamsAttachmentDownload,
  TeamsChatPrefs,
  TeamsChatPrefsResult,
  TeamsChatSummary,
  TeamsMessage,
  TeamsMessagePage,
  TeamsPresence,
  TeamsSendMessageRequest,
  TeamsBlockNode,
  TeamsPerson,
  TeamsCreateChatRequest,
  DivisionAlsoSeesMap,
  DivisionBrandMap,
  HiddenFeaturesByDivision,
  HiddenFeaturesByRole,
  UserRole,
  ProductGenerations,
  ProductGenerationLink,
  CreateNextGenerationInput,
  CreateNextGenerationResult,
  SyncfxContent,
  SyncfxPublishInput,
  SyncfxPublishResult,
  SyncfxPublishStatus,
  SyncfxVideoLookup,
} from '@atlas/shared';

import { bindCsrfTokenSource } from './preferences';
import { bindNotificationsCsrf } from './notifications';

export const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

// CSRF token stored in memory (never in localStorage — not accessible to XSS)
let csrfToken: string | null = null;

/**
 * Read the in-memory CSRF token.
 *
 * Exists for callers that cannot go through `request()` — the standards bot
 * streams an NDJSON response and needs the raw Response object, which
 * `request()` consumes. Kept a getter rather than exporting the variable so the
 * token still lives in one place and never lands in localStorage.
 */
export function getCsrfToken(): string | null {
  return csrfToken;
}

export function setCsrfToken(token: string) {
  csrfToken = token;
  // Propagate to the twoFactor module which has its own request helper.
  bindTwoFactorCsrf(() => csrfToken);
}

// Allow sibling service modules (preferences, etc.) to read the CSRF token
// without duplicating the auth machinery.
bindCsrfTokenSource(() => csrfToken);
bindNotificationsCsrf(() => csrfToken);

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// A 401 from these endpoints is a wrong-credentials error the calling page
// surfaces itself — it must NOT be treated as an expired session (which would
// log the user out and wipe the in-memory CSRF token).
// `/auth/logout` is here for the opposite reason: a 401 from it means the session
// is ALREADY gone. Treating it as "expired session → log out" made the app call
// logout again, whose 401 fired the event again, and a tab left open past
// expiry spun at ~10 logout requests a second until it was closed (2026-09-02:
// 4,608 in a day from one machine).
const CREDENTIAL_ENDPOINTS = new Set(['/auth/login', '/auth/change-password', '/auth/logout']);

interface ApiEnvelope {
  data?: unknown;
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
}

/**
 * Fetch a fresh CSRF token and store it.
 *
 * Deliberately a bare fetch rather than `request()`: this is called from inside
 * `request()`'s own retry path, and routing it back through would risk a loop if
 * the refresh itself ever started failing that way.
 */
async function refreshCsrfToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/csrf-token`, { credentials: 'include' });
    if (!res.ok) return false;
    const body = (await res.json()) as ApiEnvelope & { data?: { csrfToken?: string } };
    const token = body.data?.csrfToken;
    if (!token) return false;
    setCsrfToken(token);
    return true;
  } catch {
    return false;
  }
}

export async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  /**
   * Internal. Set when this call is already the retry after a CSRF refresh, so
   * a persistently rejected token surfaces as an error instead of looping.
   */
  isCsrfRetry = false
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (MUTATING_METHODS.has(method) && csrfToken) {
    headers['x-csrf-token'] = csrfToken;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include', // Send httpOnly cookies automatically
    headers,
  });

  // Parse defensively — rate-limit, CSRF, and gateway responses are not always
  // JSON. An unconditional response.json() turns those into a cryptic
  // "JSON.parse: unexpected character" instead of the real error.
  const rawBody = await response.text();
  let data: ApiEnvelope | null = null;
  if (rawBody) {
    try {
      data = JSON.parse(rawBody) as ApiEnvelope;
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    if (response.status === 401 && !CREDENTIAL_ENDPOINTS.has(endpoint)) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    // A rejected CSRF token is recoverable and not the user's problem: the token
    // rotates with the session, so a tab left open over a re-login holds a stale
    // one and every save fails until the page is reloaded. Fetch a fresh token
    // and replay the request once. Only once, and only when this attempt was not
    // already the retry.
    //
    // Replaying a mutation is safe here specifically because CSRF is enforced
    // ahead of the routes (app.ts): a rejected request never reached a handler,
    // so nothing was written the first time.
    if (response.status === 403 && data?.error?.code === 'CSRF_INVALID' && !isCsrfRetry) {
      if (await refreshCsrfToken()) {
        return request<T>(endpoint, options, true);
      }
    }

    throw new ApiError(
      response.status,
      data?.error?.code || 'UNKNOWN_ERROR',
      data?.error?.message || `Request failed (${response.status})`,
      data?.error?.details
    );
  }

  // Analytics, and only for the handful of calls `TRACKED_ACTIONS` names.
  // Deliberately after the error branch: a count here is work that succeeded.
  // It sends the event name alone — no endpoint, no id, no body.
  trackApiAction(method, endpoint);

  return data?.data as T;
}

// Products API
export const products = {
  /** Watch a product (#1038). Factory accounts get a 403. */
  getWatch: (productId: string) =>
    request<ProductWatchStatus>(`/products/${encodeURIComponent(productId)}/watch`),
  watch: (productId: string) =>
    request<ProductWatchStatus>(`/products/${encodeURIComponent(productId)}/watch`, { method: 'PUT' }),
  unwatch: (productId: string) =>
    request<ProductWatchStatus>(`/products/${encodeURIComponent(productId)}/watch`, { method: 'DELETE' }),

  list: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<PaginatedResponse<Product>>(`/products${query}`);
  },

  /**
   * Every published product, paged through to exhaustion.
   *
   * `GET /products` caps `limit` at 100, so no single request can return the
   * whole catalog. Product pickers need the full list to resolve ids to names,
   * hence the loop. Not usable for the spreadsheet, which needs hidden products
   * and the full metadata bag; use `getAllForSpreadsheet` there.
   */
  listAllPublished: async (): Promise<Product[]> => {
    const PAGE_SIZE = 100;
    const all: Product[] = [];

    for (let page = 1; ; page += 1) {
      const query = new URLSearchParams({
        status: 'published',
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      const resp = await request<PaginatedResponse<Product>>(`/products?${query}`);

      all.push(...resp.items);

      // `hasNext` is the contract, but an empty page also terminates: a server
      // that reports hasNext forever must not spin this loop.
      if (!resp.pagination.hasNext || resp.items.length === 0) break;
    }

    return all;
  },

  getAllForSpreadsheet: (includeHidden = false) =>
    request<Product[]>(`/products/spreadsheet${includeHidden ? '?includeHidden=true' : ''}`),

  /**
   * Slim all-products list projected to only the given field dot-paths. Backs
   * the /products list page so it isn't shipped the full ~300-field metadata bag.
   * `includeHidden` (admin-only, ignored server-side otherwise) surfaces hidden
   * products for the visibility-management view.
   */
  listLite: (fields: string[], includeHidden = false) =>
    request<Product[]>(
      `/products/list-lite?fields=${encodeURIComponent(fields.join(','))}${
        includeHidden ? '&includeHidden=true' : ''
      }`,
    ),

  /** Admin-only: show/hide a product across every listing + export (reversible). */
  setVisibility: (productId: string, hidden: boolean) =>
    request<Product>(`/products/${productId}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ hidden }),
    }),

  /**
   * Products with a verified-good Asana PDLC connection. Drives the /pdlc list page.
   * `pendingChecks` is the number of integrations being verified by the
   * background sweep — when non-zero the page should poll for updates.
   */
  listPdlcConnected: () =>
    request<{ items: Product[]; pendingChecks: number }>('/products/pdlc-connected'),

  get: (productId: string, version?: number) => {
    const path = version
      ? `/products/${productId}/versions/${version}`
      : `/products/${productId}`;
    return request<Product>(path);
  },

  getVersions: (productId: string) =>
    request<{ productId: string; versions: Array<{ version: number; status: string }> }>(
      `/products/${productId}/versions`
    ),

  create: (input: CreateProductInput) =>
    request<Product>('/products', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  update: (productId: string, version: number, input: Partial<CreateProductInput>) =>
    request<Product>(`/products/${productId}/versions/${version}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  addComment: (productId: string, version: number, comment: string) =>
    request<Product>(`/products/${productId}/versions/${version}/comment`, {
      method: 'PUT',
      body: JSON.stringify({ comment }),
    }),

  deprecate: (productId: string, version: number) =>
    request<Product>(`/products/${productId}/versions/${version}/deprecate`, {
      method: 'POST',
    }),

  publish: (productId: string, version: number) =>
    request<Product>(`/products/${productId}/versions/${version}/publish`, {
      method: 'POST',
    }),

  delete: (productId: string) =>
    request<void>(`/products/${productId}`, {
      method: 'DELETE',
    }),

  getImageUploadUrl: (productId: string, params: {
    filename: string;
    contentType: string;
    size: number;
  }) =>
    request<PresignedUploadResult & { fileId: string }>(
      `/products/${productId}/image/upload-url`,
      { method: 'POST', body: JSON.stringify(params) }
    ),

  confirmImageUpload: (productId: string, fileId: string) =>
    request<Product>(`/products/${productId}/image/confirm`, {
      method: 'POST',
      body: JSON.stringify({ fileId }),
    }),

  getImageDownloadUrl: (productId: string) =>
    request<{ downloadUrl: string; expiresAt: string }>(
      `/products/${productId}/image/download-url`
    ),

  deleteImage: (productId: string) =>
    request<Product>(`/products/${productId}/image`, {
      method: 'DELETE',
    }),

  listImages: (productId: string) =>
    request<{ images: Array<ProductImage & { downloadUrl: string }> }>(
      `/products/${productId}/images`
    ),

  setImageRole: (productId: string, fileId: string, role: ProductImageRole | null) =>
    request<Product>(`/products/${productId}/images/${fileId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  deleteImageFile: (productId: string, fileId: string) =>
    request<Product>(`/products/${productId}/images/${fileId}`, {
      method: 'DELETE',
    }),

  getAsanaIntegration: (productId: string) =>
    request<{
      productId: string;
      asanaPortfolioGid: string;
      asanaPortfolioName: string;
      asanaPortfolioUrl?: string;
      syncStatus: string;
    } | null>(`/products/${productId}/asana`),

  validateAsanaPortfolio: (productId: string) =>
    request<{ valid: boolean; url?: string; reason?: string }>(
      `/products/${productId}/asana/validate`
    ),

  updateAsanaIntegration: (productId: string, portfolioUrl: string) =>
    request<{
      productId: string;
      asanaPortfolioGid: string;
      asanaPortfolioName: string;
      asanaPortfolioUrl?: string;
      syncStatus: string;
    }>(`/products/${productId}/asana`, {
      method: 'PUT',
      body: JSON.stringify({ portfolioUrl }),
    }),

  removeAsanaIntegration: (productId: string) =>
    request<null>(`/products/${productId}/asana`, {
      method: 'DELETE',
    }),

  getSharePointLink: (productId: string) =>
    request<{
      productId: string;
      url: string;
      updatedBy: string;
      updatedAt: string;
    } | null>(`/products/${productId}/sharepoint`),

  updateSharePointLink: (productId: string, url: string) =>
    request<{
      productId: string;
      url: string;
      updatedBy: string;
      updatedAt: string;
    }>(`/products/${productId}/sharepoint`, {
      method: 'PUT',
      body: JSON.stringify({ url }),
    }),

  removeSharePointLink: (productId: string) =>
    request<null>(`/products/${productId}/sharepoint`, {
      method: 'DELETE',
    }),

  // Dash generations (#1295)
  getGenerations: (productId: string) =>
    request<ProductGenerations>(`/products/${productId}/generations`),

  listGenerationLinks: () => request<ProductGenerationLink[]>('/products/generation-links'),

  createNextGeneration: (productId: string, input: CreateNextGenerationInput) =>
    request<CreateNextGenerationResult>(`/products/${productId}/next-generation`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  getGitHubLink: (productId: string) =>
    request<{
      productId: string;
      url: string;
      updatedBy: string;
      updatedAt: string;
    } | null>(`/products/${productId}/github-link`),

  updateGitHubLink: (productId: string, url: string) =>
    request<{
      productId: string;
      url: string;
      updatedBy: string;
      updatedAt: string;
    }>(`/products/${productId}/github-link`, {
      method: 'PUT',
      body: JSON.stringify({ url }),
    }),

  removeGitHubLink: (productId: string) =>
    request<null>(`/products/${productId}/github-link`, {
      method: 'DELETE',
    }),

  getPdlcDashboard: (productId: string) =>
    request<PdlcDashboardResponse>(`/products/${productId}/asana/pdlc`),

  /** Tick a spreadsheet PDLC deliverable (#809). Answers with Asana's settled value. */
  setPdlcDeliverable: (productId: string, taskGid: string, completed: boolean) =>
    request<{ completed: boolean }>(`/products/${productId}/pdlc-deliverables/${taskGid}`, {
      method: 'PATCH',
      body: JSON.stringify({ completed }),
    }),

  /** PDLC timing metrics (derived from Asana; no ATLAS-side writes). */
  getPdlcMetrics: (productId: string) =>
    request<PdlcMetricsResponse>(`/products/${productId}/asana/pdlc/metrics`),

  /** One task in full, for the task-detail view. Uncached server-side. */
  getPdlcTask: (productId: string, taskGid: string) =>
    request<PdlcTask>(`/products/${productId}/asana/pdlc/tasks/${taskGid}`),

  /** Comments AND system stories, as Asana's "All activity" shows. */
  getPdlcTaskActivity: (productId: string, taskGid: string) =>
    request<AsanaProjectStory[]>(`/products/${productId}/asana/pdlc/tasks/${taskGid}/activity`),

  updatePdlcTask: (
    productId: string,
    taskGid: string,
    body: {
      completed?: boolean;
      dueOn?: string | null;
      /** Only honoured alongside dueOn — Asana treats the two as one range. */
      startOn?: string | null;
      assigneeGid?: string | null;
      /** Custom Task Type status pill — an option gid. */
      statusOptionGid?: string;
      customField?: { fieldGid: string; value: string | number | string[] | null };
      description?: string;
      mentions?: AsanaMentionRef[];
    },
  ) =>
    // Answers with the read-back status only when statusOptionGid was sent.
    request<PdlcStatusReadBack | null>(`/products/${productId}/asana/pdlc/tasks/${taskGid}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  postPdlcStatus: (productId: string, input: { title: string; text: string; statusType: string }) =>
    request<null>(`/products/${productId}/asana/pdlc/status`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  getPdlcTaskComments: (productId: string, taskGid: string) =>
    request<AsanaTaskComment[]>(`/products/${productId}/asana/pdlc/tasks/${taskGid}/comments`),

  /** Every task's thread summary for one product, read live from Asana (#1117). */
  getPdlcConversations: (productId: string) =>
    request<Record<string, PdlcTaskCommentSummary>>(`/products/${productId}/asana/pdlc/conversations`),

  getMentionableUsers: (productId: string) =>
    request<AsanaMentionUser[]>(`/products/${productId}/asana/pdlc/mentionable-users`),

  postPdlcTaskComment: (
    productId: string,
    taskGid: string,
    text: string,
    mentions: AsanaMentionRef[] = [],
  ) =>
    request<AsanaTaskComment>(`/products/${productId}/asana/pdlc/tasks/${taskGid}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text, mentions }),
    }),

  // Task file uploads (#1108): SharePoint via ATLAS, in chunks.
  getPdlcTaskFiles: (productId: string, taskGid: string) =>
    request<PdlcTaskFile[]>(`/products/${productId}/pdlc/tasks/${taskGid}/files`),

  planPdlcTaskFiles: (productId: string, taskGid: string, body: PdlcTaskFilePlanRequest) =>
    request<PdlcTaskFilePlan>(`/products/${productId}/pdlc/tasks/${taskGid}/files/plan`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Deliverables (#1108): what each PDLC task should produce, and its state.
  getPdlcDeliverables: (productId: string) =>
    request<PdlcDeliverable[]>(`/products/${productId}/pdlc/deliverables`),

  /** Link tasks only. An empty url clears it. */
  setPdlcTaskLink: (productId: string, taskGid: string, body: SetPdlcTaskLinkInput) =>
    request<{ url: string; setBy: string; setAt: string }>(`/products/${productId}/pdlc/tasks/${taskGid}/link`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  requestPdlcFile: (productId: string, taskGid: string, note?: string) =>
    request<PdlcFileRequestResult>(`/products/${productId}/pdlc/tasks/${taskGid}/request-file`, {
      method: 'POST',
      body: JSON.stringify(note ? { note } : {}),
    }),

  nudgePdlcTask: (productId: string, taskGid: string) =>
    request<PdlcFileRequestResult>(`/products/${productId}/pdlc/tasks/${taskGid}/nudge`, { method: 'POST' }),

  getPdlcTaskFileDownload: (productId: string, fileId: string) =>
    request<{ url: string }>(`/products/${productId}/pdlc/files/${encodeURIComponent(fileId)}/download`),

  getPdlcTaskAttachments: (productId: string, taskGid: string) =>
    request<AsanaAttachment[]>(
      `/products/${productId}/asana/pdlc/tasks/${taskGid}/attachments`,
    ),

  // Same-origin URL that streams an attachment's bytes for inline <video>/<img>
  // preview (cookie-authed automatically). See asanaService.getAttachmentStream.
  pdlcAttachmentStreamUrl: (productId: string, taskGid: string, attachmentGid: string): string =>
    `${API_BASE}/products/${productId}/asana/pdlc/tasks/${taskGid}/attachments/${encodeURIComponent(attachmentGid)}/stream`,

  // multipart upload — the shared request() helper forces application/json,
  // so this calls fetch directly and lets the browser set the multipart
  // boundary. CSRF + cookie auth are still applied.
  uploadPdlcTaskAttachment: async (
    productId: string,
    taskGid: string,
    file: File,
  ): Promise<AsanaAttachment> => {
    const endpoint = `/products/${productId}/asana/pdlc/tasks/${taskGid}/attachments`;
    const form = new FormData();
    form.append('file', file);

    const headers: Record<string, string> = {};
    if (csrfToken) {
      headers['x-csrf-token'] = csrfToken;
    }

    const response = await fetch(
      `${API_BASE}${endpoint}`,
      { method: 'POST', credentials: 'include', headers, body: form },
    );

    const rawBody = await response.text();
    let data: ApiEnvelope | null = null;
    if (rawBody) {
      try {
        data = JSON.parse(rawBody) as ApiEnvelope;
      } catch {
        data = null;
      }
    }

    if (!response.ok) {
      if (response.status === 401 && !CREDENTIAL_ENDPOINTS.has(endpoint)) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      throw new ApiError(
        response.status,
        data?.error?.code || 'UNKNOWN_ERROR',
        data?.error?.message || `Request failed (${response.status})`,
        data?.error?.details,
      );
    }

    return data?.data as AsanaAttachment;
  },

  getPdlcUnreadCounts: (productId: string) =>
    request<PdlcUnreadCounts>(`/products/${productId}/asana/pdlc/unread-counts`),

  markPdlcTaskRead: (productId: string, taskGid: string) =>
    request<{ ok: boolean }>(`/products/${productId}/asana/pdlc/tasks/${taskGid}/read`, {
      method: 'POST',
    }),
};

// Submissions API
export const submissions = {
  list: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return request<PaginatedResponse<EvaluationSubmission>>(`/submissions${query}`);
  },

  get: (submissionId: string) =>
    request<SubmissionWithTemplate>(`/submissions/${submissionId}`),

  create: (input: CreateSubmissionInput) =>
    request<EvaluationSubmission>('/submissions', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  autosave: (submissionId: string, data: AutosaveRequest) =>
    request<AutosaveResponse>(`/submissions/${submissionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /** Throws ApiError VALIDATION_ERROR (422, `details.errors` lists the fields) when required questions are blank. */
  submit: (submissionId: string) =>
    request<{ submittedAt: string }>(`/submissions/${submissionId}/submit`, { method: 'POST' }),

  lock: (submissionId: string, reason?: string) =>
    request<{ lockedAt: string }>(`/submissions/${submissionId}/lock`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  unlock: (submissionId: string, reason: string) =>
    request<{ unlockedAt: string }>(`/submissions/${submissionId}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  setStatus: (submissionId: string, status: string, reason?: string) =>
    request<{ status: string; updatedAt: string }>(`/submissions/${submissionId}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, reason }),
    }),

  /** Assignee, reviewer and due date (#1039). An absent key is left alone; `null` clears it. */
  setAssignment: (submissionId: string, input: UpdateSubmissionAssignmentInput) =>
    request<EvaluationSubmission>(`/submissions/${submissionId}/assignment`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  delete: (submissionId: string) =>
    request<void>(`/submissions/${submissionId}`, {
      method: 'DELETE',
    }),

  // Snapshot / Revision History
  listSnapshots: (submissionId: string, params?: { page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return request<PaginatedResponse<SnapshotListItem>>(`/submissions/${submissionId}/snapshots${qs ? `?${qs}` : ''}`);
  },

  getSnapshot: (submissionId: string, revision: number) =>
    request<SubmissionSnapshot>(`/submissions/${submissionId}/snapshots/${revision}`),

  restoreSnapshot: (submissionId: string, revision: number, reason: string) =>
    request<{ restoredFromRevision: number; restoredAt: string }>(`/submissions/${submissionId}/snapshots/${revision}/restore`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

// Files API
export const files = {
  getUploadUrl: (submissionId: string, params: {
    fieldPath: string;
    filename: string;
    contentType: string;
    size: number;
  }) =>
    request<PresignedUploadResult & { fileId: string }>(
      `/submissions/${submissionId}/files/upload-url`,
      { method: 'POST', body: JSON.stringify(params) }
    ),

  directUpload: async (submissionId: string, params: {
    fieldPath: string;
    filename: string;
    contentType: string;
    file: File | Blob;
  }): Promise<FileMetadata> => {
    const url = `${API_BASE}/submissions/${submissionId}/files/upload?fieldPath=${encodeURIComponent(params.fieldPath)}&filename=${encodeURIComponent(params.filename)}&contentType=${encodeURIComponent(params.contentType)}`;

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': params.contentType,
        ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
      },
      body: params.file,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `Upload failed: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  },

  confirmUpload: (submissionId: string, fileId: string) =>
    request<FileMetadata>(`/submissions/${submissionId}/files/confirm`, {
      method: 'POST',
      body: JSON.stringify({ fileId }),
    }),

  getDownloadUrl: (submissionId: string, fileId: string) =>
    request<{ downloadUrl: string; expiresAt: string }>(
      `/submissions/${submissionId}/files/${fileId}/download-url`
    ),

  delete: (submissionId: string, fileId: string) =>
    request<void>(`/submissions/${submissionId}/files/${fileId}`, {
      method: 'DELETE',
    }),
};

// Users API
export const users = {
  /** Products the current user watches, PM-derived included (#1038). */
  watching: () => request<WatchedProduct[]>('/users/me/watching'),

  list: () =>
    request<User[]>('/users'),

  /** Authenticated user directory — safe subset, optional role filter. */
  directory: (role?: string) => {
    const query = role ? `?role=${encodeURIComponent(role)}` : '';
    return request<UserDirectoryEntry[]>(`/users/directory${query}`);
  },

  get: (userId: string) =>
    request<User>(`/users/${userId}`),

  create: (input: CreateUserInput) =>
    request<User>('/users', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  update: (userId: string, input: Partial<CreateUserInput>) =>
    request<User>(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  resetPassword: (userId: string, input: { newPassword: string }) =>
    request<{ success: boolean }>(`/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  unlock: (userId: string) =>
    request<User>(`/users/${userId}/unlock`, {
      method: 'POST',
    }),

  delete: (userId: string) =>
    request<{ success: boolean }>(`/users/${userId}`, {
      method: 'DELETE',
    }),

  getProductLayout: (productId: string) =>
    request<{ cardOrder: ProductCardId[] | null }>(
      `/users/me/product-layout/${encodeURIComponent(productId)}`,
    ),

  setProductLayout: (productId: string, cardOrder: ProductCardId[]) =>
    request<{ cardOrder: ProductCardId[] }>(
      `/users/me/product-layout/${encodeURIComponent(productId)}`,
      { method: 'PUT', body: JSON.stringify({ cardOrder }) },
    ),

  getTheme: () =>
    request<{ themeId: ThemeId | null }>('/users/me/theme'),

  setTheme: (themeId: ThemeId) =>
    request<{ themeId: ThemeId }>('/users/me/theme', {
      method: 'PUT',
      body: JSON.stringify({ themeId }),
    }),

  getDivision: () =>
    request<{ division: DivisionId | null }>('/users/me/division'),

  setDivision: (division: DivisionId) =>
    request<{ division: DivisionId }>('/users/me/division', {
      method: 'PUT',
      body: JSON.stringify({ division }),
    }),

  /** Resolved division scope for the current user: { assigned, isOrg, active }. */
  getEffectiveDivisions: () =>
    request<EffectiveDivisions>('/users/me/divisions/effective'),

  getLanguage: () =>
    request<{ language: string }>('/users/me/language'),

  setLanguage: (language: string) =>
    request<{ language: string }>('/users/me/language', {
      method: 'PUT',
      body: JSON.stringify({ language }),
    }),

  getNotificationSound: () =>
    request<{ enabled: boolean | null }>('/users/me/notification-sound'),

  setNotificationSound: (enabled: boolean) =>
    request<{ enabled: boolean }>('/users/me/notification-sound', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  getHideEmptyCompetitorFields: () =>
    request<{ hide: boolean | null }>('/users/me/hide-empty-competitor-fields'),

  setHideEmptyCompetitorFields: (hide: boolean) =>
    request<{ hide: boolean }>('/users/me/hide-empty-competitor-fields', {
      method: 'PUT',
      body: JSON.stringify({ hide }),
    }),

  getHomeProductSections: () =>
    request<{ sections: Record<string, boolean> | null }>(
      '/users/me/home-product-sections',
    ),

  setHomeProductSections: (sections: Record<string, boolean>) =>
    request<{ sections: Record<string, boolean> }>(
      '/users/me/home-product-sections',
      { method: 'PUT', body: JSON.stringify({ sections }) },
    ),

  getHomeSectionOrder: () => request<{ order: string[] | null }>('/users/me/home-section-order'),

  setHomeSectionOrder: (order: string[]) =>
    request<{ order: string[] }>('/users/me/home-section-order', {
      method: 'PUT',
      body: JSON.stringify({ order }),
    }),

  getSidebarOrder: () =>
    request<{ order: string[] | null; previousOrder: string[] | null }>('/users/me/sidebar-order'),

  setSidebarOrder: (order: string[]) =>
    request<{ order: string[] }>('/users/me/sidebar-order', {
      method: 'PUT',
      body: JSON.stringify({ order }),
    }),
};

// Push API
export const push = {
  vapidPublicKey: () =>
    request<{ enabled: boolean; publicKey: string | null }>('/push/vapid-public-key'),
  subscribe: (endpoint: string, keys: { p256dh: string; auth: string }) =>
    request<undefined>('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint, keys }),
    }),
  unsubscribe: (endpoint: string) =>
    request<undefined>('/push/unsubscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
};

// Auth API
export const auth = {
  login: (input: LoginRequest) =>
    request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  logout: () =>
    request<null>('/auth/logout', { method: 'POST' }),

  me: () =>
    request<{ user: UserContext }>('/auth/me'),

  getCsrfToken: () =>
    request<{ csrfToken: string }>('/auth/csrf-token'),

  changePassword: (input: ChangePasswordRequest) =>
    request<ChangePasswordResponse>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// Taxonomies API
export const taxonomies = {
  get: (productId: string) =>
    request<ProductTaxonomy | null>(`/taxonomies/${productId}`),

  update: (productId: string, input: UpdateProductTaxonomyInput) =>
    request<ProductTaxonomy>(`/taxonomies/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  delete: (productId: string) =>
    request<void>(`/taxonomies/${productId}`, {
      method: 'DELETE',
    }),

  list: (filters?: {
    type?: string;
    status?: string;
    family?: string;
    market?: string;
  }) => {
    const query = filters ? `?${new URLSearchParams(filters as Record<string, string>)}` : '';
    return request<ProductTaxonomy[]>(`/taxonomies${query}`);
  },

  listSnapshots: (productId: string) =>
    request<TaxonomySnapshotListItem[]>(`/taxonomies/${productId}/snapshots`),

  getSnapshot: (productId: string, revision: number) =>
    request<TaxonomySnapshot>(`/taxonomies/${productId}/snapshots/${revision}`),
};

// API Keys API
export const apiKeys = {
  list: () =>
    request<ApiKeyDisplay[]>('/api-keys'),

  get: (keyId: string) =>
    request<ApiKeyDisplay>(`/api-keys/${keyId}`),

  create: (input: CreateApiKeyInput) =>
    request<CreateApiKeyResponse>('/api-keys', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  update: (keyId: string, input: UpdateApiKeyInput) =>
    request<ApiKeyDisplay>(`/api-keys/${keyId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  revoke: (keyId: string) =>
    request<ApiKeyDisplay>(`/api-keys/${keyId}/revoke`, {
      method: 'POST',
    }),

  delete: (keyId: string) =>
    request<void>(`/api-keys/${keyId}`, {
      method: 'DELETE',
    }),
};

/**
 * Lightweight user directory entry returned by /users/directory.
 * Available to any authenticated user — no sensitive fields.
 */
export interface UserDirectoryEntry {
  userId: string;
  name: string;
  email: string;
  roles: string[];
  title?: string;
  department?: string;
}

// Search API — powers the Cmd-K command palette
export interface SearchHit {
  type: 'product' | 'submission' | 'user' | 'fixture';
  id: string;
  label: string;
  description?: string;
  href: string;
}

export const search = {
  query: (q: string) =>
    request<{ results: SearchHit[] }>(`/search?q=${encodeURIComponent(q)}`),
};

// Asana user connection API
export const asana = {
  getConnection: () => request<AsanaConnectionStatus>('/auth/asana/connection'),
  disconnect: () => request<null>('/auth/asana/connection', { method: 'DELETE' }),
  getMyPdlcTasks: () => request<MyPdlcTask[]>('/auth/asana/my-pdlc-tasks'),
  getHandedOffPdlcTasks: () => request<MyPdlcTask[]>('/auth/asana/handed-off-pdlc-tasks'),
  claimProjects: () => request<AsanaClaimRun>('/auth/asana/claim-projects', { method: 'POST' }),
  getClaimRun: () => request<AsanaClaimRun>('/auth/asana/claim-projects'),
  // Authenticated XHR — returns the Asana authorize URL to navigate to.
  // Omit productId to connect from the profile page; the callback then redirects to /profile.
  getConnectUrl: (productId?: string, returnTo?: string) => {
    const q = new URLSearchParams();
    if (productId) q.set('productId', productId);
    if (returnTo) q.set('returnTo', returnTo);
    const qs = q.toString();
    return request<{ authorizeUrl: string }>(`/auth/asana/connect${qs ? `?${qs}` : ''}`);
  },
};

// Microsoft Teams per-user connection API.
// The connect flow is a full-page OAuth redirect: fetch the authorize URL with
// the session's credentials, then navigate to it. Never a popup or a fetch.
export const teams = {
  getStatus: () => request<TeamsConnectionStatus>('/teams/status'),
  disconnect: () => request<null>('/teams/connection', { method: 'DELETE' }),
  getConnectUrl: () => request<TeamsAuthorizeUrl>('/auth/microsoft/teams/connect'),

  /**
   * The user's recent Teams conversations, newest activity first.
   * A 409 (`ApiError.status`) means the stored connection is dead and the user
   * must reconnect — the caller must NOT render this as "no chats".
   */
  listChats: (params?: { limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    const qs = query.toString();
    return request<TeamsChatSummary[]>(`/teams/chats${qs ? `?${qs}` : ''}`);
  },

  /**
   * Clear a conversation's unread badge in ATLAS.
   *
   * Local only — it does not mark the chat read in the user's real Teams
   * client, because Graph offers no API to move its own read watermark. Sends
   * no timestamp: the server stamps it.
   */
  markChatRead: (chatId: string) =>
    request<{ readAt: string }>(`/teams/chats/${encodeURIComponent(chatId)}/read`, {
      method: 'PUT',
    }),

  /** Clear every conversation's unread badge in ATLAS. Local only, like `markChatRead`. */
  markAllChatsRead: () => request<{ readAt: string }>('/teams/chats/read', { method: 'PUT' }),

  /**
   * Favourite or mute one conversation.
   *
   * Stored against the user on the server, so the setting follows them to
   * another browser or machine. Send only the flag being changed — an omitted
   * one is left alone, which is what keeps two quick menu clicks from undoing
   * each other.
   *
   * ATLAS-only: it changes nothing in the user's real Teams client.
   */
  setChatPrefs: (chatId: string, prefs: TeamsChatPrefs) =>
    request<TeamsChatPrefsResult>(`/teams/chats/${encodeURIComponent(chatId)}/prefs`, {
      method: 'PUT',
      body: JSON.stringify(prefs),
    }),
   /**
   * Resolve a chat attachment to a short-lived download URL.
   *
   * Fetched on the click, never ahead of time: the URL Microsoft returns is
   * pre-authenticated, so it is a credential with a short life and does not
   * belong in a message payload sitting in a cache.
   */
  getAttachmentDownload: (chatId: string, messageId: string, attachmentId: string) =>
    request<TeamsAttachmentDownload>(
      `/teams/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}` +
        `/attachments/${encodeURIComponent(attachmentId)}/download`,
    ),

  /**
   * One page of messages for a conversation, newest first. Pass the previous
   * page's `nextCursor` to walk further back; its absence ends the history.
   */
  listMessages: (chatId: string, params?: { limit?: number; cursor?: string }) => {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.cursor) query.set('cursor', params.cursor);
    const qs = query.toString();
    return request<TeamsMessagePage>(
      `/teams/chats/${encodeURIComponent(chatId)}/messages${qs ? `?${qs}` : ''}`,
    );
  },

  /**
   * Post a message to a conversation as the signed-in user, returning the
   * message Graph actually created — the caller replaces its optimistic echo
   * with this. A 409 means the connection is dead, exactly as on the reads.
   */
  sendMessage: (chatId: string, text: string, nodes: TeamsBlockNode[]) => {
    // `nodes` is sent ALWAYS, even for a message with no formatting. Whether the
    // document needs HTML is the server's call (`teamsNodesNeedHtml`), and it
    // can only make it if it has the document — a client that decided for itself
    // would be a second, divergent copy of that rule.
    //
    // `text` still travels: it is the plain-text rendering used for the
    // optimistic echo and as the body when no HTML is needed.
    const body: TeamsSendMessageRequest = { text, nodes };
    return request<TeamsMessage>(`/teams/chats/${encodeURIComponent(chatId)}/messages`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Availability for the people shown in the sidebar, batched into one call.
   * Ids are Entra object ids — the `presenceUserId` of the 1:1 chats on screen.
   *
   * A GET with a comma-joined list, matching the route: it is a read, and this
   * app CSRF-protects POST, which a repeating background poll should not have
   * to carry. The server caps the list at `PRESENCE_BATCH_LIMIT` and rejects an
   * over-long one outright, so callers must not exceed it.
   *
   * The response omits ids Graph declined to report on — including every id
   * when the token predates the presence consent. A missing entry means "no
   * dot", never an error.
   */
  getPresence: (userIds: string[]) =>
    request<TeamsPresence[]>(`/teams/presence?ids=${encodeURIComponent(userIds.join(','))}`),

  /**
   * People you can start a chat with or @mention.
   *
   * NOT a directory search. The server derives these from the signed-in user's
   * OWN chat memberships, because searching the tenant directory needs
   * `User.ReadBasic.All`, which IT has not consented (see
   * `docs/IT_TODO_SIDEBAR.md`). Every caller must present it that way: someone
   * you have never messaged genuinely will not be here, and telling the user
   * otherwise turns a permission boundary into an apparent bug.
   *
   * Server-side each call can cost several Graph round trips, so callers must
   * debounce rather than fire per keystroke.
   */
  listPeople: (params?: { q?: string; limit?: number; chatId?: string }) => {
    const query = new URLSearchParams();
    if (params?.q) query.set('q', params.q);
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    // Present for the @mention popup, absent for the new-chat picker: one asks
    // "who is in this conversation", the other "who do I talk to at all".
    if (params?.chatId) query.set('chatId', params.chatId);
    const qs = query.toString();
    return request<TeamsPerson[]>(`/teams/people${qs ? `?${qs}` : ''}`);
  },

  /**
   * Start — or reopen — a 1:1 with someone, returning the conversation.
   *
   * Idempotent by Graph's own semantics: a 1:1 between two people is a single
   * chat, so calling this for somebody you already talk to hands back the
   * existing conversation rather than creating a duplicate.
   */
  createChat: (userId: string) => {
    const body: TeamsCreateChatRequest = { userId };
    return request<TeamsChatSummary>('/teams/chats', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
};

export const feedback = {
  reportIssue: (input: ReportIssueRequest) =>
    request<ReportIssueResponse>('/feedback/report-issue', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Open issues shown above the report form so people can spot duplicates (#1021). */
  openIssues: () => request<ReportedIssueList>('/feedback/issues'),
};

// ─── Bug Tracker (PLM bugs + tech-writing intake against Asana) ────────────

export interface SubmitBugRequest {
  productId: string;
  name: string;
  details: string;
  detailsHtml?: string;
  /** Primary Category — the one Engineering - Master carries. */
  category?: BugTrackerCategory;
  photometrics?: BugTrackerPhotometrics;
  priority: BugTrackerPriority;
}

export interface SubmitTechWritingRequest {
  /** An Atlas product. Omit it and pass `productName` for one Atlas has no row for. */
  productId?: string;
  /** Free-typed product name, used only when `productId` is absent. */
  productName?: string;
  name: string;
  details: string;
  detailsHtml?: string;
  twTags: BugTrackerTwTag[];
  priority: BugTrackerPriority;
}

export interface ListBugsFilters {
  section?: BugTrackerSection;
  sourceProject?: BugTrackerSourceProject;
  category?: BugTrackerCategory;
  priority?: BugTrackerPriority;
  productId?: string;
  assigneeGid?: string;
  completed?: boolean;
  /** Free-text match against the task title. */
  search?: string;
  limit?: number;
}

export const bugTracker = {
  submitBug: (input: SubmitBugRequest) =>
    request<BugTrackerTask>('/bug-tracker/bugs', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  submitTechWriting: (input: SubmitTechWritingRequest) =>
    request<BugTrackerTask>('/bug-tracker/tech-writing', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  list: (filters: ListBugsFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.section) params.set('section', filters.section);
    if (filters.sourceProject) params.set('sourceProject', filters.sourceProject);
    if (filters.category) params.set('category', filters.category);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.productId) params.set('productId', filters.productId);
    if (filters.assigneeGid) params.set('assigneeGid', filters.assigneeGid);
    if (filters.search) params.set('search', filters.search);
    if (typeof filters.completed === 'boolean') params.set('completed', String(filters.completed));
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return request<BugTrackerTask[]>(`/bug-tracker/bugs${qs ? `?${qs}` : ''}`);
  },

  get: (gid: string) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}`),

  getSubtasks: (gid: string) =>
    request<BugTrackerTask[]>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/subtasks`),

  listComments: (gid: string) =>
    request<AsanaTaskComment[]>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/comments`),

  postComment: (gid: string, text: string, mentions: AsanaMentionRef[] = []) =>
    request<AsanaTaskComment>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text, mentions }),
    }),

  listAttachments: (gid: string) =>
    request<AsanaAttachment[]>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/attachments`),

  // Same-origin URL that streams an attachment's bytes for inline <video>/<img>
  // preview (cookie-authed automatically). See asanaService.getAttachmentStream.
  attachmentStreamUrl: (gid: string, attachmentGid: string): string =>
    `${API_BASE}/bug-tracker/bugs/${encodeURIComponent(gid)}/attachments/${encodeURIComponent(attachmentGid)}/stream`,

  // Export a bug "to email" (issue #186): fetch the PDF summary + attachments
  // bundle as a binary blob and return it with the server-supplied filename.
  // Cookie-authed like every other GET; the caller triggers the browser save.
  exportBug: async (gid: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await fetch(
      `${API_BASE}/bug-tracker/bugs/${encodeURIComponent(gid)}/export`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      let message = `Export failed (${response.status})`;
      try {
        const data = (await response.json()) as ApiEnvelope;
        if (data?.error?.message) message = data.error.message;
      } catch {
        // Non-JSON error body — keep the status-based message.
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = match?.[1] ?? `bug-${gid}.zip`;
    return { blob, filename };
  },

  // Multipart upload — uses fetch directly so the browser sets the multipart
  // boundary header. Mirrors uploadPdlcTaskAttachment (~L304).
  uploadAttachment: async (gid: string, file: File): Promise<AsanaAttachment> => {
    const endpoint = `/bug-tracker/bugs/${encodeURIComponent(gid)}/attachments`;
    const form = new FormData();
    form.append('file', file);

    const headers: Record<string, string> = {};
    if (csrfToken) headers['x-csrf-token'] = csrfToken;

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: form,
    });

    const rawBody = await response.text();
    let data: ApiEnvelope | null = null;
    if (rawBody) {
      try { data = JSON.parse(rawBody) as ApiEnvelope; } catch { data = null; }
    }

    if (!response.ok) {
      if (response.status === 401 && !CREDENTIAL_ENDPOINTS.has(endpoint)) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      throw new ApiError(
        response.status,
        data?.error?.code || 'UNKNOWN_ERROR',
        data?.error?.message || `Request failed (${response.status})`,
        data?.error?.details,
      );
    }

    return data?.data as AsanaAttachment;
  },

  setAssignee: (gid: string, assigneeGid: string | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/assignee`, {
      method: 'PATCH',
      body: JSON.stringify({ assigneeGid }),
    }),

  setDueDate: (gid: string, dueOn: string | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/due-date`, {
      method: 'PATCH',
      body: JSON.stringify({ dueOn }),
    }),

  setName: (gid: string, name: string) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/name`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  setDetails: (gid: string, details: string | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/details`, {
      method: 'PATCH',
      body: JSON.stringify({ details }),
    }),

  setResolution: (gid: string, resolution: string | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/resolution`, {
      method: 'PATCH',
      body: JSON.stringify({ resolution }),
    }),

  setSendUpdate: (gid: string, value: 'Yes' | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/send-update`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    }),

  setSupport: (gid: string, value: BugTrackerSupport | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/support`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    }),

  setTwTags: (gid: string, values: BugTrackerTwTag[]) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/tw`, {
      method: 'PATCH',
      body: JSON.stringify({ values }),
    }),

  /** Set TW Status (Tech Writing's own progress). null clears it. */
  setTwStatus: (gid: string, value: BugTrackerTwStatus | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/tw-status`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    }),

  /** Move the task between the Engineering - Master sections. */
  setSection: (gid: string, section: BugTrackerSection) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/section`, {
      method: 'PATCH',
      body: JSON.stringify({ section }),
    }),

  setPendingTags: (gid: string, values: BugTrackerPendingTag[]) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/pending`, {
      method: 'PATCH',
      body: JSON.stringify({ values }),
    }),

  /** Set the Priority enum. Admin/PM/engineer only server-side. */
  setPriority: (gid: string, value: BugTrackerPriority | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/priority`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    }),

  /** Set the Custom Task Type status (top-left pill). Admin/PM only server-side. */
  setStatus: (gid: string, optionGid: string) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ optionGid }),
    }),

  setPortfolio: (gid: string, portfolioGid: string | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/portfolio`, {
      method: 'PATCH',
      body: JSON.stringify({ portfolioGid }),
    }),

  setProduct: (gid: string, productId: string | null) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/product`, {
      method: 'PATCH',
      body: JSON.stringify({ productId }),
    }),

  setCompleted: (gid: string, completed: boolean) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/completed`, {
      method: 'PATCH',
      body: JSON.stringify({ completed }),
    }),

  setBoardRank: (gid: string, rank: number) =>
    request<BugTrackerTask>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/rank`, {
      method: 'PATCH',
      body: JSON.stringify({ rank }),
    }),

  searchPortfolios: (q: string) => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return request<{ gid: string; name: string }[]>(`/bug-tracker/portfolios/search${qs}`);
  },

  markRead: (gid: string) =>
    request<{ ok: true }>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/mark-read`, {
      method: 'POST',
    }),

  getMentionableUsers: (gid: string) =>
    request<AsanaMentionUser[]>(`/bug-tracker/bugs/${encodeURIComponent(gid)}/mentionable-users`),

  getUnreadCounts: (taskGids: string[]) => {
    if (taskGids.length === 0) return Promise.resolve<PdlcUnreadCounts>({});
    const qs = new URLSearchParams({ taskGids: taskGids.join(',') }).toString();
    return request<PdlcUnreadCounts>(`/bug-tracker/unread-counts?${qs}`);
  },
};

/** Every signature request, across every product (#837). Role-gated: rows carry share tokens. */
export const signingRequests = {
  overview: () => request<SigningRequestOverviewRow[]>('/signing-requests'),
};

/** The home page's "Your documents" panel (#837). Scoped to the caller by the server. */
export const myDocuments = {
  get: () => request<MyDocumentsView>('/my-documents'),
};

export const suppliers = {
  list: (opts: { status?: 'active' | 'archived' | 'all' } = {}) => {
    const query = opts.status ? `?status=${opts.status}` : '';
    return request<Supplier[]>(`/suppliers${query}`);
  },
  get: (supplierId: string) =>
    request<Supplier>(`/suppliers/${supplierId}`),
  create: (input: CreateSupplierInput, opts: { force?: boolean } = {}) =>
    request<Supplier>(`/suppliers${opts.force ? '?force=true' : ''}`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  update: (supplierId: string, input: UpdateSupplierInput) =>
    request<Supplier>(`/suppliers/${supplierId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  delete: (supplierId: string) =>
    request<Supplier>(`/suppliers/${supplierId}`, { method: 'DELETE' }),
  addContact: (supplierId: string, input: CreateSupplierContactInput) =>
    request<Supplier>(`/suppliers/${supplierId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateContact: (supplierId: string, contactId: string, input: UpdateSupplierContactInput) =>
    request<Supplier>(`/suppliers/${supplierId}/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  removeContact: (supplierId: string, contactId: string) =>
    request<Supplier>(`/suppliers/${supplierId}/contacts/${contactId}`, {
      method: 'DELETE',
    }),
};

export const offices = {
  list: (opts: { status?: 'active' | 'archived' | 'all' } = {}) => {
    const query = opts.status ? `?status=${opts.status}` : '';
    return request<Office[]>(`/offices${query}`);
  },
  get: (officeId: string) =>
    request<Office>(`/offices/${officeId}`),
  create: (input: CreateOfficeInput) =>
    request<Office>('/offices', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  update: (officeId: string, input: UpdateOfficeInput) =>
    request<Office>(`/offices/${officeId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  archive: (officeId: string) =>
    request<Office>(`/offices/${officeId}`, { method: 'DELETE' }),
};

export const shipments = {
  list: (filters: ShipmentListFilters = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    }
    const q = params.toString();
    return request<Shipment[]>(`/shipments${q ? `?${q}` : ''}`);
  },
  get: (shipmentId: string) =>
    request<Shipment>(`/shipments/${shipmentId}`),
  create: (input: CreateShipmentInput) =>
    request<Shipment>('/shipments', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  update: (shipmentId: string, input: UpdateShipmentInput) =>
    request<Shipment>(`/shipments/${shipmentId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  delete: (shipmentId: string) =>
    request<null>(`/shipments/${shipmentId}`, { method: 'DELETE' }),
  resumePolling: (shipmentId: string) =>
    request<Shipment>(`/shipments/${shipmentId}/resume-polling`, {
      method: 'POST',
    }),
  pollNow: (shipmentId: string) =>
    request<Shipment>(`/shipments/${shipmentId}/poll-now`, {
      method: 'POST',
    }),
  /** Check a unit in as received by PD. Also fires the notification email. */
  markPdReceived: (shipmentId: string) =>
    request<Shipment>(`/shipments/${shipmentId}/pd-received`, {
      method: 'POST',
    }),
  /** Undo a check-in. Does not re-send (or un-send) the notification email. */
  clearPdReceived: (shipmentId: string) =>
    request<Shipment>(`/shipments/${shipmentId}/pd-received`, {
      method: 'DELETE',
    }),
};

/** Which bulletin list pre-fills the NPSO fill-out request dialog (issue #691). */
export const npsoRequestList = {
  get: () => request<{ npsoRequestListId: string | null }>('/settings/npso-request-list'),
  set: (npsoRequestListId: string | null) =>
    request<{ npsoRequestListId: string | null }>('/settings/npso-request-list', {
      method: 'PUT',
      body: JSON.stringify({ npsoRequestListId }),
    }),
};

/** Days after an NPSO fill-out request goes out before its daily reminder starts (#738). */
export const formRequestReminders = {
  get: () => request<{ delayDays: number }>('/settings/form-request-reminders'),
  set: (delayDays: number) =>
    request<{ delayDays: number }>('/settings/form-request-reminders', {
      method: 'PUT',
      body: JSON.stringify({ delayDays }),
    }),
};

/** Which bulletin list is emailed on a shipment PD check-in (issue #455). */
export const shipmentReceivedList = {
  get: () => request<{ shipmentReceivedListId: string | null }>('/settings/shipment-received-list'),
  set: (shipmentReceivedListId: string | null) =>
    request<{ shipmentReceivedListId: string | null }>('/settings/shipment-received-list', {
      method: 'PUT',
      body: JSON.stringify({ shipmentReceivedListId }),
    }),
};

// Feature Visibility API — global feature kill-switch, plus a per-role overlay.
// `getHidden` is resolved server-side FOR THE CALLER; the by-role endpoints are
// admin-only and return the raw configuration the admin UI edits.
export const featureVisibility = {
  getHidden: () => request<{ hiddenFeatures: string[] }>('/settings/feature-visibility'),
  setHidden: (hiddenFeatures: string[]) =>
    request<{ hiddenFeatures: string[] }>('/settings/feature-visibility', {
      method: 'PUT',
      body: JSON.stringify({ hiddenFeatures }),
    }),
  getByRole: () =>
    request<FeatureVisibilityConfig>('/settings/feature-visibility/by-role'),
  setForRole: (role: UserRole, hiddenFeatures: string[]) =>
    request<FeatureVisibilityConfig>('/settings/feature-visibility/by-role', {
      method: 'PUT',
      body: JSON.stringify({ role, hiddenFeatures }),
    }),
  setForDivision: (division: DivisionId, hiddenFeatures: string[]) =>
    request<FeatureVisibilityConfig>('/settings/feature-visibility/by-division', {
      method: 'PUT',
      body: JSON.stringify({ division, hiddenFeatures }),
    }),
  setDivisionBrands: (division: DivisionId, brands: string[]) =>
    request<FeatureVisibilityConfig>('/settings/division-brands', {
      method: 'PUT',
      body: JSON.stringify({ division, brands }),
    }),
  setDivisionAlsoSees: (division: DivisionId, divisions: DivisionId[]) =>
    request<FeatureVisibilityConfig>('/settings/division-also-sees', {
      method: 'PUT',
      body: JSON.stringify({ division, divisions }),
    }),
};

export interface FeatureVisibilityConfig {
  hiddenFeatures: string[];
  hiddenFeaturesByRole: HiddenFeaturesByRole;
  hiddenFeaturesByDivision: HiddenFeaturesByDivision;
  /** Division → brands it may see. Seed-merged server-side; `org` is never a key. */
  divisionBrands: DivisionBrandMap;
  /** Division → other divisions whose brands it may also see. One hop, not transitive. */
  divisionAlsoSees: DivisionAlsoSeesMap;
  /** Every brand products actually carry, incl. `NO_BRAND_KEY` when any has none. */
  knownBrands: string[];
}

// Bug-tracker subtask policy — admin-configurable; readable by any authenticated user
export const bugTrackerSettings = {
  get: () => request<BugTrackerSubtaskPolicy>('/settings/bug-tracker'),
  set: (policy: BugTrackerSubtaskPolicy) =>
    request<BugTrackerSubtaskPolicy>('/settings/bug-tracker', {
      method: 'PUT',
      body: JSON.stringify(policy),
    }),
};

/**
 * PD Dev team dashboard (#422). `issues` and `config` need a dashboard role;
 * `repos` is admin-only — it enumerates the whole org, not just tracked repos.
 */
/**
 * `Owner/Name` -> `Owner/Name` as two encoded path segments.
 *
 * Encoding the whole string would turn the slash into `%2F`, which Express
 * decodes back into one param and the route would never match.
 */
function repoPath(repo: string): string {
  return repo.split('/').map(encodeURIComponent).join('/');
}

export const devDashboard = {
  issues: (query: DevIssueQuery = {}) => {
    const params = new URLSearchParams();
    if (query.state) params.set('state', query.state);
    if (query.search) params.set('search', query.search);
    if (query.assignee) params.set('assignee', query.assignee);
    if (query.repos?.length) params.set('repos', query.repos.join(','));
    if (query.labels?.length) params.set('labels', query.labels.join(','));
    const qs = params.toString();
    return request<DevIssueListResult>(`/dev-dashboard/issues${qs ? `?${qs}` : ''}`);
  },
  getConfig: () => request<DevDashboardConfig>('/dev-dashboard/config'),
  listRepos: () => request<DevDashboardRepoOption[]>('/dev-dashboard/repos'),
  setTrackedRepos: (trackedRepos: string[]) =>
    request<DevDashboardConfig>('/dev-dashboard/repos', {
      method: 'PUT',
      body: JSON.stringify({ trackedRepos }),
    }),

  // --- Issue detail and writes -------------------------------------------
  // `owner/repo` is split into two path segments to match the route, which
  // avoids an encoded slash inside a single Express param.

  getIssue: (repo: string, number: number) =>
    request<DevIssueDetailResult>(`/dev-dashboard/issues/${repoPath(repo)}/${number}`),

  listAssignees: (repo: string) =>
    request<DevIssueAssignableUser[]>(`/dev-dashboard/repos/${repoPath(repo)}/assignees`),

  /**
   * Post a comment. A 400 with `details.code === 'github-not-connected'` means
   * the user has not linked a GitHub account — render the connect prompt, not
   * a generic failure.
   */
  addComment: (repo: string, number: number, body: string) =>
    request<DevIssueComment>(`/dev-dashboard/issues/${repoPath(repo)}/${number}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  setAssignees: (repo: string, number: number, assignees: string[]) =>
    request<{ assignees: string[] }>(
      `/dev-dashboard/issues/${repoPath(repo)}/${number}/assignees`,
      { method: 'PUT', body: JSON.stringify({ assignees }) },
    ),

  setState: (repo: string, number: number, state: DevIssueState) =>
    request<{ state: DevIssueState }>(`/dev-dashboard/issues/${repoPath(repo)}/${number}/state`, {
      method: 'PUT',
      body: JSON.stringify({ state }),
    }),

  // --- App metrics strip ---------------------------------------------------

  /**
   * Store and usage figures for the dashboard header. Served from a stored
   * snapshot, so this returns fast even when a refresh is running behind it.
   */
  appMetrics: () => request<AppMetricsResult>('/dev-dashboard/app-metrics'),

  /**
   * Trend series for the Metrics tab. `null` when no snapshot has been built
   * yet — the tab renders its empty state rather than treating that as an error.
   */
  appMetricsTrends: () =>
    request<AppMetricsTrends | null>('/dev-dashboard/app-metrics/trends'),

  /** Force a re-walk of every source. Admin-only on the server. */
  refreshAppMetrics: () =>
    request<AppMetricsResult>('/dev-dashboard/app-metrics/refresh', { method: 'POST' }),

  // --- Project board (GitHub Projects v2) ---------------------------------

  getProjectBoard: () => request<DevProjectBoard>('/dev-dashboard/project'),

  /**
   * Move a card. `statusOptionId: null` means the "No status" column.
   *
   * Same `github-not-connected` contract as the issue writes: the move goes out
   * under the user's own GitHub token, so an unconnected user gets a 400 with
   * that code rather than a generic failure.
   */
  setProjectItemStatus: (itemId: string, statusOptionId: string | null) =>
    request<{ statusOptionId: string | null }>(
      `/dev-dashboard/project/items/${encodeURIComponent(itemId)}/status`,
      { method: 'PUT', body: JSON.stringify({ statusOptionId }) },
    ),

  listProjects: () => request<DevProjectOption[]>('/dev-dashboard/projects'),

  setTrackedProject: (number: number) =>
    request<DevDashboardConfig>('/dev-dashboard/project', {
      method: 'PUT',
      body: JSON.stringify({ number }),
    }),
};

/** Bugsnag tab of the Dev Dashboard. */
export const bugsnag = {
  errors: (query: BugsnagErrorQuery = {}) => {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.releaseStage) params.set('releaseStage', query.releaseStage);
    if (query.search) params.set('search', query.search);
    if (query.projectIds?.length) params.set('projectIds', query.projectIds.join(','));
    const qs = params.toString();
    return request<BugsnagErrorListResult>(`/dev-dashboard/bugsnag/errors${qs ? `?${qs}` : ''}`);
  },

  getError: (projectId: string, errorId: string) =>
    request<{ detail: BugsnagErrorDetail; fetchedAt: string; cached: boolean }>(
      `/dev-dashboard/bugsnag/errors/${encodeURIComponent(projectId)}/${encodeURIComponent(errorId)}`,
    ),

  /** Fix / snooze / ignore / reopen / discard. Returns the ATLAS audit record. */
  triage: (projectId: string, errorId: string, body: BugsnagTriageRequest) =>
    request<BugsnagTriageRecord>(
      `/dev-dashboard/bugsnag/errors/${encodeURIComponent(projectId)}/${encodeURIComponent(errorId)}/triage`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  /**
   * Open a GitHub issue from an error. A 400 with
   * `details.code === 'github-not-connected'` means the user has not linked a
   * GitHub account — same contract as the GitHub tab's comment box.
   */
  createGithubIssue: (
    projectId: string,
    errorId: string,
    body: CreateIssueFromBugsnagRequest,
  ) =>
    request<{ htmlUrl: string; number: number }>(
      `/dev-dashboard/bugsnag/errors/${encodeURIComponent(projectId)}/${encodeURIComponent(errorId)}/github-issue`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  listProjects: () => request<BugsnagProjectOption[]>('/dev-dashboard/bugsnag/projects'),

  setTrackedProjects: (projects: TrackedBugsnagProject[]) =>
    request<DevDashboardConfig>('/dev-dashboard/bugsnag/projects', {
      method: 'PUT',
      body: JSON.stringify({ projects }),
    }),
};

/** Per-user GitHub connection, for dev-dashboard writes. */
export const github = {
  getConnection: () => request<GithubConnectionStatus>('/auth/github/connection'),
  disconnect: () => request<null>('/auth/github/connection', { method: 'DELETE' }),
  // Authenticated XHR — returns the GitHub authorize URL to navigate to.
  getConnectUrl: () => request<{ authorizeUrl: string }>('/auth/github/connect'),
};

// Product visibility management — admin-configurable; readable by any authenticated user
export const productVisibilitySettings = {
  get: () =>
    request<{ productVisibilityManagement: boolean }>('/settings/product-visibility'),
  set: (productVisibilityManagement: boolean) =>
    request<{ productVisibilityManagement: boolean }>('/settings/product-visibility', {
      method: 'PUT',
      body: JSON.stringify({ productVisibilityManagement }),
    }),
};

// Daily PDLC "your turn" email switch — admin only
export const pdlcDigestSettings = {
  get: () => request<{ pdlcDigestEnabled: boolean }>('/settings/pdlc-digest'),
  set: (pdlcDigestEnabled: boolean) =>
    request<{ pdlcDigestEnabled: boolean }>('/settings/pdlc-digest', {
      method: 'PUT',
      body: JSON.stringify({ pdlcDigestEnabled }),
    }),
};

/**
 * Whole-system backup to SharePoint — admin only.
 *
 * There is no `restore`. `inspect` reports what a restore would change and
 * writes nothing; the real procedure is docs/RESTORE_RUNBOOK.md.
 */
export const backups = {
  list: () => request<BackupListResponse>('/backups'),
  /** Returns as soon as the run is scheduled — poll `list` for the outcome. */
  run: () => request<BackupRunStatus>('/backups/run', { method: 'POST' }),
  inspect: (name: string) =>
    request<InspectReport>('/backups/inspect', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  /**
   * Downloads an archive that already exists, rather than building a new one —
   * a fresh export takes ~90s and Cloudflare cuts an idle origin at 100s.
   *
   * Zip bytes, not the `{success,data}` envelope, so this cannot go through
   * `request`. Auth is the httpOnly cookie, hence `credentials`.
   */
  download: async (name: string): Promise<Blob> => {
    const res = await fetch(`${API_BASE}/backups/${encodeURIComponent(name)}/download`, {
      credentials: 'include',
    });
    if (!res.ok) {
      throw new ApiError(res.status, 'BACKUP_DOWNLOAD_FAILED', `Download failed (${res.status})`);
    }
    return res.blob();
  },
};

// Factory chases (#1204): a personal, dated reminder on one product.
export const chases = {
  mine: () => request<ProductChaseView[]>('/chases'),
  create: (productId: string, body: CreateProductChaseInput) =>
    request<ProductChaseView>(`/products/${encodeURIComponent(productId)}/chases`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (chaseId: string, body: UpdateProductChaseInput) =>
    request<null>(`/chases/${encodeURIComponent(chaseId)}`, { method: 'PATCH', body: JSON.stringify(body) }),
};

// System API — capability probes and server-side feature flags
export const system = {
  aiTranslationEnabled: () =>
    request<{ enabled: boolean }>('/messaging/translation-enabled'),
};

// Product naming (#1110)
export const naming = {
  list: (productId: string) =>
    request<NameProposal[]>(`/naming/products/${encodeURIComponent(productId)}`),
  create: (productId: string, input: CreateNameProposalInput) =>
    request<NameProposalWriteResult>(`/naming/products/${encodeURIComponent(productId)}`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  update: (id: string, input: UpdateNameProposalInput) =>
    request<NameProposalWriteResult>(`/naming/proposals/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  queue: () => request<NameProposalQueueItem[]>('/naming/queue'),
  renames: (productId: string) =>
    request<ProductRenameRecord[]>(`/naming/products/${encodeURIComponent(productId)}/renames`),
  retryRename: (productId: string) =>
    request<ProductRenameRecord[]>(`/naming/products/${encodeURIComponent(productId)}/renames/retry`, { method: 'POST' }),
};

// Product launch dates (#1112)
export const launchDates = {
  get: (productId: string) =>
    request<ProductLaunchDates>(`/launch-dates/products/${encodeURIComponent(productId)}`),
  set: (productId: string, input: SetLaunchDateInput) =>
    request<ProductLaunchDate>(`/launch-dates/products/${encodeURIComponent(productId)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
};

// Claim caveats (#1114)
export const claimCaveats = {
  list: (productId: string) =>
    request<ClaimCaveat[]>(`/claim-caveats/products/${encodeURIComponent(productId)}`),
  create: (productId: string, input: CreateClaimCaveatInput) =>
    request<ClaimCaveat>(`/claim-caveats/products/${encodeURIComponent(productId)}`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  resolve: (id: string, input: ResolveClaimCaveatInput) =>
    request<ClaimCaveat>(`/claim-caveats/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// Launch-project setup in Asana (#1111)
const launchSetupProduct = (productId: string) => `/launch-setup/products/${encodeURIComponent(productId)}`;
export const launchSetup = {
  rules: () => request<LaunchSetupRule[]>('/launch-setup/rules'),
  /** Tasks a C or D launch drops (#1203). */
  launchTypeDrops: () => request<LaunchTypeDropSettings>('/launch-setup/launch-type-drops'),
  saveLaunchTypeDrops: (value: LaunchTypeDropSettings) =>
    request<LaunchTypeDropSettings>('/launch-setup/launch-type-drops', { method: 'PUT', body: JSON.stringify(value) }),
  saveRule: (rule: LaunchSetupRule) =>
    request<LaunchSetupRule>(`/launch-setup/rules/${encodeURIComponent(rule.brand)}`, {
      method: 'PUT',
      body: JSON.stringify(rule),
    }),
  preview: (productId: string, input: LaunchSetupInput) =>
    request<LaunchSetupPreview>(`${launchSetupProduct(productId)}/preview`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  run: (productId: string, input: LaunchSetupInput) =>
    request<LaunchSetupRun>(`${launchSetupProduct(productId)}/run`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  latestRun: (productId: string) => request<LaunchSetupRun | null>(`${launchSetupProduct(productId)}/run`),
  configured: (productId: string) => request<boolean>(`${launchSetupProduct(productId)}/configured`),
};

// Requests from PD to marketing (#1113). File uploads go through
// services/chunkedUpload with `/marketing-requests/:id/files`.
const marketingRequest = (id: string) => `/marketing-requests/${encodeURIComponent(id)}`;
export const marketingRequests = {
  brands: () => request<string[]>('/marketing-requests/brands'),
  list: (params: { status?: MarketingRequestStatus; mine?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.mine) q.set('mine', '1');
    const qs = q.toString();
    return request<MarketingRequest[]>(`/marketing-requests${qs ? `?${qs}` : ''}`);
  },
  listForProduct: (productId: string) =>
    request<MarketingRequest[]>(`/marketing-requests/products/${encodeURIComponent(productId)}`),
  create: (input: CreateMarketingRequestInput) =>
    request<MarketingRequest>('/marketing-requests', { method: 'POST', body: JSON.stringify(input) }),
  get: (id: string) => request<MarketingRequest>(marketingRequest(id)),
  update: (id: string, input: UpdateMarketingRequestInput) =>
    request<MarketingRequest>(marketingRequest(id), { method: 'PATCH', body: JSON.stringify(input) }),
  filesPath: (id: string) => `${marketingRequest(id)}/files`,
  fileDownload: (id: string, fileId: string) =>
    request<{ url: string }>(`${marketingRequest(id)}/files/${encodeURIComponent(fileId)}/download`),
  settings: () => request<MarketingRequestSettings>('/marketing-requests/settings'),
  saveSettings: (input: MarketingRequestSettings) =>
    request<MarketingRequestSettings>('/marketing-requests/settings', { method: 'PUT', body: JSON.stringify(input) }),
};

// Marketing → SyncFX app. The content lives in the SYNCFX_WEB_SERVER repo; these read it from GitHub and publish a PR.
export const syncfxContent = {
  get: () => request<SyncfxContent>('/syncfx-content'),
  video: (url: string) => request<SyncfxVideoLookup>(`/syncfx-content/video?url=${encodeURIComponent(url)}`),
  publish: (input: SyncfxPublishInput) =>
    request<SyncfxPublishResult>('/syncfx-content/publish', { method: 'POST', body: JSON.stringify(input) }),
  publishStatus: (prNumber: number) => request<SyncfxPublishStatus>(`/syncfx-content/publishes/${prNumber}`),
  /** Closes a publish whose check failed, so the next one isn't blocked behind it. */
  cancelPublish: (prNumber: number) =>
    request<SyncfxPublishStatus>(`/syncfx-content/publishes/${prNumber}/cancel`, { method: 'POST' }),
};

export const api = {
  marketingRequests,
  syncfxContent,
  naming,
  claimCaveats,
  launchSetup,
  launchDates,
  myDocuments,
  signingRequests,
  products,
  submissions,
  files,
  users,
  push,
  auth,
  taxonomies,
  apiKeys,
  suppliers,
  offices,
  shipments,
  shipmentReceivedList,
  npsoRequestList,
  formRequestReminders,
  asana,
  teams,
  search,
  feedback,
  bugTracker,
  twoFactor,
  featureVisibility,
  bugTrackerSettings,
  devDashboard,
  bugsnag,
  github,
  productVisibilitySettings,
  pdlcDigestSettings,
  backups,
  chases,
  system,
};
